import { Extension } from '@tiptap/core';
import { Plugin, PluginKey, TextSelection } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

export interface DocumentTitleOptions {
  /**
   * Placeholder text for the title
   */
  placeholder: string;
  /**
   * CSS class to add to the title element
   */
  titleClass: string;
}

/**
 * Extension that ensures the document always starts with a title block (H1).
 * The title block:
 * - Is always the first node in the document
 * - Cannot be deleted (but can be empty)
 * - Automatically created if not present
 */
export const DocumentTitle = Extension.create<DocumentTitleOptions>({
  name: 'documentTitle',

  addOptions() {
    return {
      placeholder: 'Untitled',
      titleClass: 'document-title',
    };
  },

  addProseMirrorPlugins() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const extensionThis = this;
    const titleClass = this.options.titleClass;

    return [
      new Plugin({
        key: new PluginKey('documentTitle'),

        // Add props to decorate the title node with a class and show placeholder
        props: {
          decorations(state) {
            const { doc } = state;
            const firstNode = doc.firstChild;

            if (
              firstNode &&
              firstNode.type.name === 'heading' &&
              firstNode.attrs['level'] === 1
            ) {
              const decorations = [];

              // Add a node decoration to mark this as the document title
              const nodeDecoration = Decoration.node(0, firstNode.nodeSize, {
                class: titleClass,
                'data-document-title': 'true',
              });
              decorations.push(nodeDecoration);

              // Add placeholder if the title is empty
              if (firstNode.childCount === 0 || firstNode.textContent === '') {
                const placeholderDecoration = Decoration.widget(
                  1, // Position inside the heading
                  () => {
                    const span = document.createElement('span');
                    span.className = 'placeholder document-title-placeholder';
                    // Use shorter placeholder text on small screens
                    const isSmallScreen = window.innerWidth <= 480;
                    span.textContent = isSmallScreen
                      ? 'Agent Name'
                      : extensionThis.options.placeholder;
                    return span;
                  },
                  { side: -1 }
                );
                decorations.push(placeholderDecoration);
              }

              return DecorationSet.create(doc, decorations);
            }

            return DecorationSet.empty;
          },
        },

        appendTransaction(transactions, oldState, newState) {
          const docChanged = transactions.some((tr) => tr.docChanged);
          if (!docChanged) {
            return null;
          }

          const { doc, schema } = newState;
          const headingType = schema.nodes['heading'];
          const paragraphType = schema.nodes['paragraph'];

          if (!headingType || !paragraphType) {
            return null;
          }

          // Check if the first node is an H1
          const firstNode = doc.firstChild;
          const isFirstNodeH1 =
            firstNode &&
            firstNode.type.name === 'heading' &&
            firstNode.attrs['level'] === 1;

          if (!isFirstNodeH1) {
            // First node is not an H1, so we need to fix it
            const tr = newState.tr;

            if (!firstNode) {
              // Document is empty - insert H1 and paragraph
              const title = headingType.create({ level: 1 });
              const paragraph = paragraphType.create();
              tr.insert(0, [title, paragraph]);
            } else if (
              firstNode.type.name === 'heading' &&
              firstNode.attrs['level'] !== 1
            ) {
              // First node is a heading but not H1 - convert it to H1
              tr.setNodeMarkup(0, headingType, { level: 1 });
            } else {
              // First node is not a heading - insert H1 before it
              const title = headingType.create({ level: 1 });
              tr.insert(0, title);
            }

            return tr;
          }

          return null;
        },

        filterTransaction(tr, state) {
          if (!tr.docChanged) {
            return true;
          }

          const { doc } = tr;
          const firstNode = doc.firstChild;

          // Prevent deletion of the title if it would leave us with no first node
          // or if trying to delete the only H1
          if (!firstNode) {
            return false;
          }

          const isFirstNodeH1 =
            firstNode.type.name === 'heading' && firstNode.attrs['level'] === 1;

          // If we're about to lose our H1 as the first node, reject the transaction
          if (!isFirstNodeH1 && state.doc.firstChild) {
            const wasFirstNodeH1 =
              state.doc.firstChild.type.name === 'heading' &&
              state.doc.firstChild.attrs['level'] === 1;

            // Only reject if we're losing an H1 that was there
            if (wasFirstNodeH1) {
              return false;
            }
          }

          return true;
        },
      }),
    ];
  },

  onCreate() {
    // Ensure the document has a title when the editor is created
    const { state, schema } = this.editor;
    const { doc } = state;
    const headingType = schema.nodes['heading'];
    const paragraphType = schema.nodes['paragraph'];

    if (!headingType || !paragraphType) {
      return;
    }

    const firstNode = doc.firstChild;
    const isFirstNodeH1 =
      firstNode &&
      firstNode.type.name === 'heading' &&
      firstNode.attrs['level'] === 1;

    if (!isFirstNodeH1) {
      const tr = state.tr;

      if (!firstNode) {
        // Document is empty - insert H1 and paragraph
        const title = headingType.create({ level: 1 });
        const paragraph = paragraphType.create();
        tr.insert(0, [title, paragraph]);
        // Set cursor in the title
        const $pos = tr.doc.resolve(1);
        tr.setSelection(TextSelection.near($pos));
      } else {
        // Insert H1 at the beginning
        const title = headingType.create({ level: 1 });
        tr.insert(0, title);
        // Set cursor in the title
        const $pos = tr.doc.resolve(1);
        tr.setSelection(TextSelection.near($pos));
      }

      this.editor.view.dispatch(tr);
    }
  },
});
